import { defineStore } from 'pinia'
import { ref, computed, type Ref, type ComputedRef } from 'vue'
import type { AxiosError } from 'axios'
import type { VeterinarianOffice } from '@/models/DTO/veterinarian-office/veterinarian-office.model'
import type { VeterinarianOfficeUser } from '@/models/DTO/veterinarian-office-user/veterinarian-office-user.model'
import { BaseAPIErrorModel } from '@/models/DTO/base-api-error.model'
import { VeterinarianOfficesService } from '@/services/veterinarian-offices.service'

export const useVeterinarianStore = defineStore('veterinarian', () => {
  const vetOfficesService = new VeterinarianOfficesService()

  // State
  const selectedVeterinarianOfficeId = ref<string>('')
  const veterinarianOffices: Ref<VeterinarianOffice[]> = ref([])
  const showLoading: Ref<boolean> = ref(false)
  const showButtonLoading: Ref<boolean> = ref(false)
  const showTableLoading: Ref<boolean> = ref(false)
  const errorResponse: Ref<BaseAPIErrorModel | null> = ref(null)
  const currentVeterinarianOffice: Ref<VeterinarianOffice | null> = ref(null)
  const currentVeterinarianOfficeUsers: Ref<VeterinarianOfficeUser[] | null> = ref(null)
  const selectedVeterinarianUserId = ref<string>('')
  const newVeterinarianOffice: Ref<VeterinarianOffice | null> = ref(null)

  // Getters
  const errorMessages: ComputedRef<string[]> = computed(() => {
    return errorResponse.value?.getAllErrorMessages() ?? []
  })

  const hasErrorMessages: ComputedRef<boolean> = computed(() => {
    return errorMessages.value.length > 0
  })

  // Actions - Read
  async function getVeterinarianOffices(): Promise<void> {
    try {
      showTableLoading.value = true
      errorResponse.value = null

      const response = await vetOfficesService.getAll()
      veterinarianOffices.value = [...response]
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showTableLoading.value = false
    }
  }

  async function getVeterinarianOfficeById(id: string): Promise<void> {
    try {
      showLoading.value = true
      errorResponse.value = null

      const response = await vetOfficesService.getById(id)
      currentVeterinarianOffice.value = { ...response }
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showLoading.value = false
    }
  }

  async function getAllUsersForOffice(id: string): Promise<void> {
    try {
      showTableLoading.value = true
      errorResponse.value = null

      const response = await vetOfficesService.getAllUsersForOffice(id)
      currentVeterinarianOfficeUsers.value = [...response]
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showTableLoading.value = false
    }
  }

  // Actions - Setters
  function setSelectedVeterinarianOfficeId(id: string): void {
    selectedVeterinarianOfficeId.value = id
  }

  function setSelectedVeterinarianUserId(userId: string): void {
    selectedVeterinarianUserId.value = userId
  }

  function resetVeterinarianOfficeUsers(): void {
    currentVeterinarianOfficeUsers.value = null
  }

  function setNewVeterinarianOffice(office: VeterinarianOffice | null): void {
    newVeterinarianOffice.value = office ? { ...office } : null
  }

  function resetState(): void {
    selectedVeterinarianOfficeId.value = ''
    veterinarianOffices.value = []
    showLoading.value = false
    showButtonLoading.value = false
    showTableLoading.value = false
    errorResponse.value = null
    currentVeterinarianOffice.value = null
    currentVeterinarianOfficeUsers.value = null
    selectedVeterinarianUserId.value = ''
    newVeterinarianOffice.value = null
  }

  // Actions - Write
  async function createVeterinarianOffice(model: VeterinarianOffice): Promise<void> {
    try {
      showButtonLoading.value = true
      errorResponse.value = null

      const response = await vetOfficesService.create(model)
      currentVeterinarianOffice.value = { ...response }
      getVeterinarianOffices() // fire-and-forget, no await
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showButtonLoading.value = false
    }
  }

  async function editVeterinarianOffice(model: VeterinarianOffice): Promise<void> {
    try {
      showButtonLoading.value = true
      errorResponse.value = null

      const response = await vetOfficesService.update(model)
      currentVeterinarianOffice.value = { ...response }
      getVeterinarianOffices() // fire-and-forget, no await
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showButtonLoading.value = false
    }
  }

  async function deleteVeterinarianOffice(id: string): Promise<void> {
    try {
      showLoading.value = true
      errorResponse.value = null

      const response = await vetOfficesService.delete(id)
      currentVeterinarianOffice.value = { ...response }
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showLoading.value = false
    }
  }

  return {
    // State
    selectedVeterinarianOfficeId,
    veterinarianOffices,
    showLoading,
    showButtonLoading,
    showTableLoading,
    errorResponse,
    currentVeterinarianOffice,
    currentVeterinarianOfficeUsers,
    selectedVeterinarianUserId,
    newVeterinarianOffice,

    // Getters
    errorMessages,
    hasErrorMessages,

    // Actions - Read
    getVeterinarianOffices,
    getVeterinarianOfficeById,
    getAllUsersForOffice,

    // Actions - Setters
    setSelectedVeterinarianOfficeId,
    setSelectedVeterinarianUserId,
    resetVeterinarianOfficeUsers,
    setNewVeterinarianOffice,
    resetState,

    // Actions - Write
    createVeterinarianOffice,
    editVeterinarianOffice,
    deleteVeterinarianOffice,
  }
})
